import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Warehouse, Package, ArrowDownToLine, ArrowUpFromLine, Boxes, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  findBranch, findWarehouse,
  whPurchaseOrdersOf, stockTransfersOf,
  findCatalogProduct, variantLabelOf, catalogProductsOf,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-4 py-3 flex items-center gap-3">
    <span className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: color }} aria-hidden="true">
      <Icon size={16} />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="text-base font-extrabold text-[#0d0d0d] leading-tight">{value}</p>
    </div>
  </div>
);

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
};

// Computes per-warehouse, per-product stock (received − dispatched-to-anyone),
// then highlights how much is currently allocated to *this* branch.
export const BranchStocksTab = ({ branchId, org }) => {
  const { version: dataVersion } = useSharedStore();
  const branch = useMemo(() => findBranch(branchId), [branchId, dataVersion]);
  const sourceWarehouses = useMemo(() => (
    (branch?.warehouseSourceIds || []).map((id) => findWarehouse(id)).filter(Boolean)
  ), [branch, dataVersion]);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id, dataVersion]);

  const grouped = useMemo(() => {
    return sourceWarehouses.map((wh) => {
      const byKey = new Map();
      const ensure = (productId, variantId) => {
        const key = `${productId}:${variantId || ''}`;
        let row = byKey.get(key);
        if (!row) {
          const product = findCatalogProduct(productId);
          row = {
            key,
            productId,
            variantId: variantId || '',
            productName: product?.name || productId,
            variantLabel: variantLabelOf(product, variantId),
            received: 0,
            dispatchedTotal: 0,
            toMyBranch: 0,
          };
          byKey.set(key, row);
        }
        return row;
      };

      whPurchaseOrdersOf(wh.id)
        .filter((po) => po.status === 'Received')
        .forEach((po) => {
          const row = ensure(po.productId, po.variantId);
          row.received += Number(po.qty) || 0;
        });

      stockTransfersOf(org.id)
        .filter((t) => t.fromLocationId === wh.id && t.status !== 'cancelled')
        .forEach((t) => {
          (t.lines || []).forEach((line) => {
            const product = products.find((p) => p.name === line.name);
            if (!product) return;
            const variant = (product.variants || []).find((v) => v.label === line.label);
            const row = ensure(product.id, variant?.id || '');
            row.dispatchedTotal += Number(line.qty) || 0;
            if (t.toLocationId === branchId) row.toMyBranch += Number(line.qty) || 0;
          });
        });

      const rows = Array.from(byKey.values()).map((r) => ({ ...r, onHand: Math.max(0, r.received - r.dispatchedTotal) }));
      return { warehouse: wh, rows };
    });
  }, [sourceWarehouses, org.id, products, branchId, dataVersion]);

  if (!branch) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Branch not found.
      </div>
    );
  }

  const accent = org.theme.primary;
  const totals = grouped.reduce(
    (acc, g) => {
      g.rows.forEach((r) => {
        acc.onHand += r.onHand;
        acc.toMyBranch += r.toMyBranch;
        acc.received += r.received;
      });
      return acc;
    },
    { onHand: 0, toMyBranch: 0, received: 0 },
  );

  if (sourceWarehouses.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg py-16 text-center">
        <Warehouse size={28} className="mx-auto text-slate-300" aria-hidden="true" />
        <p className="mt-3 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No source warehouses</p>
        <p className="text-[11px] font-medium text-slate-500 mt-1">Org Admin can attach warehouses to your branch from the Branches tab.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Received at warehouses" value={totals.received} icon={ArrowDownToLine} color="#0a7d3c" />
        <StatCard label="Sent to my branch" value={totals.toMyBranch} icon={ArrowUpFromLine} color={accent} />
        <StatCard label="Still on hand at WHs" value={totals.onHand} icon={Boxes} color="#b45309" />
      </section>

      {grouped.map(({ warehouse, rows }) => (
        <motion.section
          key={warehouse.id}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[var(--theme-tertiary)] rounded-3xl p-6 shadow-sm flex flex-col gap-3"
        >
          <header className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <span className="w-11 h-11 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: accent }} aria-hidden="true">
                <Warehouse size={18} />
              </span>
              <div className="min-w-0">
                <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] truncate">{warehouse.name}</h3>
                {warehouse.address && (
                  <p className="text-[11px] font-medium text-slate-500 inline-flex items-center gap-1.5">
                    <MapPin size={11} aria-hidden="true" /> {warehouse.address}
                  </p>
                )}
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: 'var(--theme-primary-soft)', color: accent }}>
              <Package size={11} aria-hidden="true" /> {rows.length} item{rows.length === 1 ? '' : 's'}
            </span>
          </header>

          {rows.length === 0 ? (
            <p className="text-[11px] font-medium text-slate-400">No stock recorded yet at this warehouse.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 border-b border-[var(--theme-tertiary)]">
                    <th className="py-2 pr-3">Product</th>
                    <th className="py-2 pr-3">Variant</th>
                    <th className="py-2 pr-3 text-right">Received</th>
                    <th className="py-2 pr-3 text-right">Out (all)</th>
                    <th className="py-2 pr-3 text-right">To my branch</th>
                    <th className="py-2 pr-3 text-right">On hand</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const product = findCatalogProduct(r.productId);
                    return (
                      <tr key={r.key} className="border-b border-[var(--theme-tertiary)] last:border-b-0">
                        <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{product?.name || r.productName}</td>
                        <td className="py-2 pr-3 text-slate-600">{r.variantLabel}</td>
                        <td className="py-2 pr-3 text-emerald-700 text-right">{r.received}</td>
                        <td className="py-2 pr-3 text-amber-700 text-right">{r.dispatchedTotal}</td>
                        <td className="py-2 pr-3 text-right font-bold" style={{ color: r.toMyBranch > 0 ? accent : '#94a3b8' }}>{r.toMyBranch}</td>
                        <td className="py-2 pr-3 text-right font-extrabold" style={{ color: r.onHand > 0 ? accent : '#94a3b8' }}>{r.onHand}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.section>
      ))}
    </div>
  );
};

BranchStocksTab.propTypes = {
  branchId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
};
